/**
 * Flights API — airport lookups for the searcher form.
 *
 * Same token/auth pattern as the packages and hotels feature calls.
 */

import { getTenantApiToken } from '../../../../core/auth/getTenantApiToken';
import { airportByIdUrl, airportSearchUrl, airportSuggestionsUrl } from './flights.endpoints';
import type { Airport, AirportSuggestionsResponse } from './flights.types';

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const token = await getTenantApiToken();

  if (!token) {
    throw new Error('flights.api.missingAuthToken');
  }

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`flights.api.requestFailed:${response.status}`);
  }

  return (await response.json()) as T;
}

/** Drops rows the form could not search with (no city code or no name). */
export function normalizeAirports(rows: Airport[] | null | undefined): Airport[] {
  if (!Array.isArray(rows)) return [];

  return rows.filter(
    (row) => Boolean(row?.cityCode) && Boolean(String(row?.cityName || '').trim()),
  );
}

export async function searchAirports(search: string, signal?: AbortSignal): Promise<Airport[]> {
  const rows = await getJson<Airport[]>(airportSearchUrl(search), signal);
  return normalizeAirports(rows);
}

/** Default origin/destination lists shown before the user types anything. */
export async function fetchAirportSuggestions(
  signal?: AbortSignal,
): Promise<{ origin: Airport[]; destination: Airport[] }> {
  const body = await getJson<AirportSuggestionsResponse>(airportSuggestionsUrl(), signal);

  return {
    destination: normalizeAirports(body?.destination),
    origin: normalizeAirports(body?.origin),
  };
}

export async function fetchAirportById(id: number, signal?: AbortSignal): Promise<Airport | null> {
  const airport = await getJson<Airport>(airportByIdUrl(id), signal);
  return airport?.cityCode ? airport : null;
}
